import {
  Dialog,
  DialogContent,
  Stack,
  Backdrop,
  CircularProgress,
  Box,
} from "@mui/material";
import { Formik, Form } from "formik";
import COAConfigurations from "./COAConfigurations";
import PayrollBasicInfoSection from "./PayrollBasicInfoSection";
import PayrollStatusSection from "./PayrollStatusSection";
import { useSnackbar, SnackbarProvider } from "notistack";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { savePayrollSetup, deletePayrollSetup } from "../../../api";
import shortcutManager from "../../../Utilities/ShortcutManager";
import { PayrollValidationSchema } from "../../../Validations/PayrollValidation";
import {
  buildSavePayrollSetupPayload,
  buildUpdatePayrollSetupPayload,
} from "../../../Payloads/PayrollSetupPayload";
import ClonePropertiesPopup from "../../../Components/ClonePropertiesPopup";
import PayrollsPopup from "../../../Components/PayrollPopup";
import AuditLogIcon from "../../../Images/Audit.png";
import AuditLog from "../../../AuditLog/AuditLog";
import { FormToolbar } from "../../../Components/FormToolbar";
import { ITEM_TYPE_OPTIONS } from "../../../Enums/Enum";
import {
  hasDuplicateDepartmentJobTitle,
  hasInconsistentDepartmentJobTitle,
} from "../../../HelperFunctions/HelperFunctions";
import { useAlerts } from "../../../Hooks";
import { useParams } from "react-router-dom";
import { usePayrollItemData } from "../../../Hooks/usePayrollItemData";
import { payrollSetupStyles } from "../../../styles/payrollSetup.styles";

// --- Validation Helper ---
const validatePayrollForm = (values, setFieldTouched, enqueueSnackbar, showErrorPopup) => {
  if (!values.corporation?.corpValue) {
    enqueueSnackbar("Corporation must be selected", { variant: "error" });
    return false;
  }
  if (!values.itemName?.trim()) {
    setFieldTouched("itemName", true, false);
    enqueueSnackbar("Item Name is required", { variant: "error" });
    return false;
  }
  if (!values.itemType) {
    setFieldTouched("itemType", true, false);
    enqueueSnackbar("Item Type is required", { variant: "error" });
    return false;
  }
  if (values.itemType === "wages") {
    if (!values.wageType) {
      setFieldTouched("wageType", true, false);
      enqueueSnackbar("Wage Type is required for Wages", { variant: "error" });
      return false;
    }
    if (!values.payType) {
      setFieldTouched("payType", true, false);
      enqueueSnackbar("Pay Type is required for Wages", { variant: "error" });
      return false;
    }
  }
  if (values.updateCOA && !values.updateCOADate) {
    enqueueSnackbar("Please select date to change the COA", { variant: "error" });
    return false;
  }
  if (!values.entries || values.entries.length === 0) {
    showErrorPopup("At least one mapping row is required.");
    return false;
  }
  for (let i = 0; i < values.entries.length; i++) {
    const entry = values.entries[i];
    if (!entry.department) {
      setFieldTouched(`entries[${i}].department`, true, false);
      enqueueSnackbar("Department is required", { variant: "error" });
      return false;
    }
    if (values.itemType === "wages" && !entry.jobTitle) {
      setFieldTouched(`entries[${i}].jobTitle`, true, false);
      enqueueSnackbar("Job Title is required for Wages", { variant: "error" });
      return false;
    }
    if (!entry.expenseCoa) {
      setFieldTouched(`entries[${i}].expenseCoa`, true, false);
      enqueueSnackbar("Expense Account is required", { variant: "error" });
      return false;
    }
    if (values.itemType === "employer_taxes" && !entry.payableCoa) {
      setFieldTouched(`entries[${i}].payableCoa`, true, false);
      enqueueSnackbar("Payable Account is required", { variant: "error" });
      return false;
    }
  }
  if (hasDuplicateDepartmentJobTitle(values.entries)) {
    showErrorPopup("Duplicate row detected. Each Department/Job Title combination must be unique.");
    return false;
  }
  if (hasInconsistentDepartmentJobTitle(values.entries)) {
    showErrorPopup("Job Title is required");
    return false;
  }
  return true;
};

const AddNewPayroll = ({
  open,
  onClose,
  editData,
  onDeleteSuccess,
  onSuccess,
  prefillData,
  permissions,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [actionLoading, setActionLoading] = useState(false);
  const submitActionRef = useRef("close");
  const submitFormRef = useRef(null);
  const formikRef = useRef(null);
  const [clonePopupOpen, setClonePopupOpen] = useState(false);
  const [selectedCloneCorps, setSelectedCloneCorps] = useState([]);
  const [auditOpen, setAuditOpen] = useState(false);
  
  const originalDeptJobRef = useRef([]); // Will store a deep copy of initial entries
  const isPrefilled = Boolean(prefillData && Object.keys(prefillData).length > 0);
  const saveOptions = useMemo(() => isPrefilled ? ["Save & Close"] : ["Save & New", "Save & Close"], [isPrefilled]);
  const [openSaveOptions, setOpenSaveOptions] = useState(false);
  const saveAnchorRef = useRef(null);
  const [selectedSaveIndex, setSelectedSaveIndex] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  // Keep action in sync to prevent "Save & Close" from persisting when re-opened with prefill data
  useEffect(() => {
    const action = saveOptions[selectedSaveIndex] === "Save & New" ? "new" : "close";
    submitActionRef.current = action;
  }, [selectedSaveIndex, saveOptions, open]);

  const { corpId: urlCorpId } = useParams();
  const effectiveCorpId = prefillData?.corpId || urlCorpId;
  const {
    popup: alertPopup,
    showErrorPopup,
    showDeleteConfirm,
    showCancelConfirm,
    hidePopup,
  } = useAlerts();

  const { corporations, wageTypes, payTypes, formValues, detailsLoading } =
    usePayrollItemData(open, editData, effectiveCorpId, showErrorPopup, prefillData);

  useEffect(() => {
    if (open && corporations?.length > 0) {
      if (editData) {
        originalDeptJobRef.current = JSON.parse(JSON.stringify(formValues.entries || []));
      } else {
        originalDeptJobRef.current = [];
      }

      const allCorps = corporations.filter((c) => c.corpValue !== "").map((c) => c.corpValue);
      setSelectedCloneCorps(allCorps);
    }
  }, [open, corporations, editData, formValues.entries]); 

  const adjustedFormValues = useMemo(() => {
    let baseValues = { ...formValues, clone: true };
    if (prefillData && !editData ) {
      let mappedItemType = "";
      
      if (prefillData.itemTypeNumber) {
        const num = parseInt(prefillData.itemTypeNumber, 10);
        const matchedOption = ITEM_TYPE_OPTIONS.find(opt => opt.id === num);
        if (matchedOption) mappedItemType = matchedOption.value;
      }

      let selectedCorpId = prefillData.corpId || "";
      if (selectedCorpId === "null" || selectedCorpId === "undefined") {
        selectedCorpId = "";
      }

      let foundCorp = null;
      if (selectedCorpId && corporations?.length > 0) {
        foundCorp = corporations.find(c => String(c.corpValue).toLowerCase() === String(selectedCorpId).toLowerCase());
        if (foundCorp) {
          selectedCorpId = foundCorp.corpValue;
        }
      }

      return {
        ...baseValues,
        corporation: foundCorp || {
          corpValue: selectedCorpId,
          corpName: prefillData.corpName || ""
        },
        itemType: mappedItemType || formValues.itemType || "",
        itemName: prefillData.itemName || formValues.itemName || "",
        entries: [
          {
            department: prefillData.departmentId || "",
            jobTitle: prefillData.jobId || "",
            expenseCoa: "",
            payableCoa: "",
          },
        ],
      };
    }
    return baseValues;
  }, [formValues, prefillData, editData, corporations]);

  const [coaDateKey, setCoaDateKey] = useState(0);
  
  const [confirmCoaDateChange, setConfirmCoaDateChange] = useState({
    open: false,
    onConfirm: () => {},
    onCancel: () => {},
  });

  const [confirmUpdate, setConfirmUpdate] = useState({
    open: false,
    onConfirm: () => {},
  });

  const isLoading = detailsLoading || actionLoading;

  const handleClose = useCallback((dirty) => {
    if (dirty) {
      showCancelConfirm(
        "Are you sure you want to cancel? All unsaved changes will be lost.",
        () => {
          onClose();
        },
      );
    } else {
      onClose(); // Close immediately if there are no unsaved changes
    }
  }, [onClose, showCancelConfirm]);

  const handleDelete = useCallback(() => {
    if (permissions && !permissions.canDelete) {
      enqueueSnackbar("You don't have permission to delete this payroll item.", { variant: "error" });
      return;
    }

    showDeleteConfirm(
      "Are you sure you want to delete this payroll item?",
      async () => {
        try {
          setActionLoading(true);
          const response = await deletePayrollSetup(editData.payrollItemId);
          setActionLoading(false);

          if (response?.Status === "Success" || response?.StatusCode === 200) {
            enqueueSnackbar(response?.Status, { variant: "success" });
            onClose();
            if (onDeleteSuccess) onDeleteSuccess();
          } else {
            enqueueSnackbar(response?.Status, { variant: "error" });
          }
        } catch (err) {
          setActionLoading(false);
          enqueueSnackbar("An error occurred while deleting.", { variant: "error" });
        }
      },
    );
  }, [permissions, enqueueSnackbar, showDeleteConfirm, editData?.payrollItemId, onClose, onDeleteSuccess]);

  useEffect(() => {
    if (open) {
      setSelectedSaveIndex(0); // Ensure the dropdown index resets when opened
    }
  }, [open]);

  // 2. Keyboard Shortcuts
  useEffect(() => {
    if (!open) return;
    shortcutManager.removeAll();
    shortcutManager.addShortcut("alt+n", () => {
      if (prefillData) return; // Disable 'Save & New' shortcut in map missing item mode
      submitActionRef.current = "new";
      if (!editData && submitFormRef.current) submitFormRef.current();
    });
    shortcutManager.addShortcut("alt+s", () => {
      submitActionRef.current = "close";
      if (submitFormRef.current) submitFormRef.current();
    });
    shortcutManager.addShortcut("alt+u", () => {
      submitActionRef.current = "close";
      if (editData && submitFormRef.current) submitFormRef.current();
    });
    shortcutManager.addShortcut("alt+l", () => {
      if (editData) handleDelete();
    });
    shortcutManager.addShortcut("alt+c", () => {
      handleClose(formikRef.current?.dirty);
    });
    shortcutManager.addShortcut("alt+f5", () => {
      setRefreshKey((prev) => prev + 1);
      if (formikRef.current) formikRef.current.resetForm();
    });
    return () => shortcutManager.removeAll();
  }, [open, onClose, editData, prefillData, handleClose, handleDelete]);

  // useEffect(() => {
  //   debugger;
  //   // Only control the main application menu if this is not a pre-filled popup.
  //   // The parent dialog will handle the menu visibility in that case.
  //   if (open && !isPrefilled) {
  //     window.parent.postMessage("HideMenu", "*");}
    

  //   return () => {
  //     if (!isPrefilled) window.parent.postMessage("ShowMenu", "*");
  //   };
  // }, [open, isPrefilled]);

  
  useEffect(() => {
    // When this view is active, ensure the main application menu is hidden.
    window.parent.postMessage("HideMenu", "*");
    return () => {
      if (!isPrefilled) window.parent.postMessage("ShowMenu", "*");
    };
    // The parent component (PayPeriodSetup) is responsible for showing the menu on final close.
    // So, no cleanup is needed here.
  }, [isPrefilled]);

  // const SLOTPROPS = {
  //     input: {
  //         sx: { paddingTop: "10px", paddingBottom: "10px", display: "flex", alignItems: "center" }
  //     }
  // };

  // --- CORE SAVE PROCESS ---
  const processSave = async (payload, resetForm, isCloning) => {
    try {
      setActionLoading(true);
      let response;
      if (editData) response = await savePayrollSetup(payload);
      else response = await savePayrollSetup(payload);

      setActionLoading(false);

      // A. Success Case
      if (
        response &&
        (response.Status === "Success" || response.StatusCode === 200)
      ) {
        const successMsg = editData
          ?  "Payroll updated successfully!"
          :  "Payroll Saved successfully!";

        enqueueSnackbar(successMsg, { variant: "success" });

        if (onSuccess) onSuccess(response?.ID); // Refresh grid immediately!

        // Delay the close/reset to allow user to see the success snackbar
        setTimeout(() => {
          if (submitActionRef.current === "close") {
            onClose();
          } else if (submitActionRef.current === "new" && resetForm) {
            resetForm();
          }
        }, 1000);
        return;
      }

      // Fallback for other errors
      enqueueSnackbar( response?.Status || "Unknown error", { variant: "error" }
      );
    } catch (error) {
      setActionLoading(false);
      console.error("Save error:", error);
      enqueueSnackbar(`An error occurred: ${error.message}`, { variant: "error" });
    }
  };

  // --- UNIFIED FORM SUBMISSION HANDLER (combines validation & submit) ---
  const handleInitialSubmit = async (
    values,
    { resetForm, setFieldTouched },
  ) => {
    if (!validatePayrollForm(values, setFieldTouched, enqueueSnackbar, showErrorPopup)) {
      return;
    }

    const originalEntries = originalDeptJobRef.current || [];
    if (editData) {
      const currentEntries = values.entries;
      let needsConfirmation = false;
      const wasActive = formikRef.current?.initialValues?.active;

      for (const currentEntry of currentEntries) {
        // Find the original state of this row using its id
        const originalEntry = originalEntries.find(e => e.id === currentEntry.id);

        if (originalEntry && currentEntry.isUsedEmp) {
          const deptChanged = originalEntry.department !== currentEntry.department;
          const jobChanged = originalEntry.jobTitle !== currentEntry.jobTitle;
          const expenseCoaChanged = originalEntry.expenseCoa !== currentEntry.expenseCoa;
          const payableCoaChanged = originalEntry.payableCoa !== currentEntry.payableCoa;

          if (deptChanged || jobChanged || expenseCoaChanged || payableCoaChanged) {
            needsConfirmation = true;
            break; // Found one change, no need to check further
          }
        }
      }

      // Also check if the active status has changed (either to active or inactive)
      if (wasActive !== values.active && values.itemType === "wages" && values.entries.some(e => e.isUsedEmp)) {
        needsConfirmation = true;
      }

      if (needsConfirmation) {
        setConfirmUpdate({
          open: true,
          onConfirm: () => proceedWithSubmit(values, resetForm, originalEntries),
        });
        return; // Stop here and wait for user confirmation
      }
    }

    // ===== BUILD PAYLOAD & SAVE =====
    let payload;
    if (editData) {
      payload = buildUpdatePayrollSetupPayload(
        values,
        editData,
        selectedCloneCorps,
        originalEntries // Pass the original entries for row-by-row comparison
      );
    } else {
      payload = buildSavePayrollSetupPayload(
        values,
        selectedCloneCorps,
        wageTypes,
        payTypes,
      );
    }

    let isCloning = false;
    if (values.clone) {
      const sourceCorpId = values.corporation?.corpValue;
      const cloneCorpIds = Array.isArray(selectedCloneCorps)
        ? selectedCloneCorps.filter((id) => String(id) !== String(sourceCorpId))
        : [];
      isCloning = cloneCorpIds.length > 0;
    }

    await processSave(payload, resetForm, isCloning);
  };

  const proceedWithSubmit = async (values, resetForm, originalEntries) => {
    setConfirmUpdate({ open: false, onConfirm: () => {} });

    let payload;
    if (editData) {
      payload = buildUpdatePayrollSetupPayload(
        values,
        editData,
        selectedCloneCorps,
        originalEntries
      );
    } else {
      // This path is unlikely if confirmation was needed, but included for completeness
      payload = buildSavePayrollSetupPayload(
        values,
        selectedCloneCorps,
        wageTypes,
        payTypes,
      );
    }
    await processSave(payload, resetForm, false);
  };

  const handleSaveMenuItemClick = (event, index) => {
    setSelectedSaveIndex(index);
    setOpenSaveOptions(false);
    const action = saveOptions[index] === "Save & New" ? "new" : "close";
    submitActionRef.current = action;
    if (submitFormRef.current) {
      submitFormRef.current();
    }
  };

  const handleSaveToggle = () => {
    setOpenSaveOptions((prevOpen) => !prevOpen);
  };

  const handleSaveOptionsClose = (event) => {
    // Allow closing the menu via the Escape key
    if (event?.type === 'keydown' && event.key !== 'Escape') {
      return;
    }
    if (event?.target && saveAnchorRef.current && saveAnchorRef.current.contains(event.target)) {
      return;
    }
    setOpenSaveOptions(false);
  };

  return (
    <SnackbarProvider
      maxSnack={1}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      autoHideDuration={3000}
    >
    <Dialog
      open={open}
      fullScreen
      onClose={onClose}
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
    >
      <Backdrop
        sx={payrollSetupStyles.backdrop}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Formik
        key={refreshKey}
        innerRef={formikRef}
        initialValues={adjustedFormValues}
        enableReinitialize={true}
        validationSchema={PayrollValidationSchema}
        onSubmit={handleInitialSubmit}
      >
        {({
          values,
          setFieldValue,
          setFieldTouched,
          handleChange,
          submitForm,
          errors,
          touched,
          resetForm,
          dirty,
          handleBlur,
        }) => {
          submitFormRef.current = () => {
            if (validatePayrollForm(values, setFieldTouched, enqueueSnackbar, showErrorPopup)) {
              submitForm();
            }
          };

          const getGridVisibility = (itemType) => {
            switch (itemType) {
              case "deductions":
              case "employee_taxes":
                return { showExpenseCol: true, showPayableCol: false };
              case "additions":
                return { showExpenseCol: false, showPayableCol: true };
              default: // Wages, Employer Taxes
                return { showExpenseCol: true, showPayableCol: true };
            }
          };
          const { showExpenseCol, showPayableCol } = getGridVisibility(
            values.itemType,
          );

          return (
            <>
              <FormToolbar
                key={saveOptions.join("-")} // Forces the toolbar to remount if options change
                editData={editData}
                title={editData ? "Edit Payroll Item" : "Add New Payroll Item"}
                selectedSaveIndex={selectedSaveIndex}
                saveOptions={saveOptions}
                handleSaveToggle={handleSaveToggle}
                handleSaveOptionsClose={handleSaveOptionsClose}
                handleSaveMenuItemClick={handleSaveMenuItemClick}
                openSaveOptions={openSaveOptions}
                saveAnchorRef={saveAnchorRef}
                handleUpdate={() => {
                  submitActionRef.current = "close";
                  if (submitFormRef.current) submitFormRef.current();
                }}
                handleDelete={handleDelete}
                handleClose={handleClose}
                dirty={dirty}
                setAuditOpen={setAuditOpen}
                auditIcon={AuditLogIcon}
              />

              <DialogContent sx={payrollSetupStyles.addNewDialogContent}>
                <Form id="payrollForm" style={{ height: "100%" }}>
                  <Stack gap={2} height={"100%"}>
                    <PayrollBasicInfoSection
                      values={values}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      handleBlur={handleBlur}
                      corporations={corporations}
                      wageTypes={wageTypes}
                      payTypes={payTypes}
                      isEditMode={Boolean(editData)}
                      prefillData={prefillData}
                      enqueueSnackbar={enqueueSnackbar}
                    />

                    <PayrollStatusSection
                      values={values}
                      setFieldValue={setFieldValue}
                      isEditMode={Boolean(editData)}
                      corporations={corporations}
                      selectedCloneCorps={selectedCloneCorps}
                      setSelectedCloneCorps={setSelectedCloneCorps}
                      setClonePopupOpen={setClonePopupOpen}
                      coaDateKey={coaDateKey}
                      setCoaDateKey={setCoaDateKey}
                      setConfirmCoaDateChange={setConfirmCoaDateChange}
                    />

                    <Box overflow={"auto"}>
                      <COAConfigurations
                        corpId={values.corporation?.corpValue}
                        initialEntries={values.entries}
                        onChange={(data) => setFieldValue("entries", data)}
                        itemType={values.itemType}
                        showExpenseColumn={showExpenseCol}
                        showPayableColumn={showPayableCol}
                        prefillData={prefillData}
                      />
                    </Box>
                  </Stack>
                </Form>
              </DialogContent>
              <ClonePropertiesPopup
                open={clonePopupOpen}
                onClose={() => setClonePopupOpen(false)}
                corporations={corporations.filter((c) => c.corpValue !== values.corporation?.corpValue && c.corpValue !== "")}
                selectedCorpIds={selectedCloneCorps.filter(id => String(id) !== String(values.corporation?.corpValue))}
                onSelect={setSelectedCloneCorps}
              />
            </>
          );
        }}
      </Formik>

      <PayrollsPopup
        open={alertPopup.open}
        popupType={alertPopup.type}
        message={alertPopup.message}
        onConfirm={alertPopup.onConfirm}
        onCancel={hidePopup}
      />
      <PayrollsPopup
        open={confirmUpdate.open}
        popupType="cancel"
        message={
          formikRef.current?.values?.updateCOA
            ? "Details will be updated into employee master with impacting the posted payroll."
            : "Details will be updated into employee master without impacting the posted payroll."
        }
        onConfirm={confirmUpdate.onConfirm}
        onCancel={() => {
          setConfirmUpdate({ open: false, onConfirm: () => {} });
        }}
      />
      <PayrollsPopup
        open={confirmCoaDateChange.open}
        popupType="cancel"
        message="Are you sure to continue the changes?"
        onConfirm={confirmCoaDateChange.onConfirm}
        onCancel={confirmCoaDateChange.onCancel}
      />

      <AuditLog
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        auditType="Payroll"
        id={editData?.payrollItemId}
      />
    </Dialog>
    </SnackbarProvider>
  );
};

export default AddNewPayroll;
